import { useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import { CMSImage } from "common/blocks/Image/cms-image";
import MediaPlayer from "common/blocks/MediaPlayer";
import { NextPrevButton } from "common/blocks/NextPrevButton";
import UIIcon from "common/blocks/UIIcon";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import { EventNames, usePub, useSub } from "hooks/usePublishSubscribe";
import { ResolvedImage } from "resolvers/data/imageResolver";
import { VehicleService } from "services/vehicle/vehicle";
import Swiper from "swiper";
import { FreeMode, Keyboard, Navigation, Thumbs } from "swiper/modules";
import { Swiper as CarouselSwiper, SwiperSlide } from "swiper/react";

import styles from "./ImageViewer.module.scss";
import MobileScrollGallery from "./components/MobileScrollGallery";
import ThumbsSwiper from "./components/ThumbsSwiper";

const cx = classNames.bind(styles);

let timer: NodeJS.Timeout;

export type ImageViewerMode = "Gallery" | "360";

export type ImageViewerProps = {
    cmsMedia?: ResolvedImage[];
    media?: VehicleService.Imagery;
    mode?: ImageViewerMode;
    activeIndex?: number;
};

const ImageViewer = ({ media, cmsMedia, mode = "Gallery", activeIndex = 0 }: ImageViewerProps) => {
    const videoHasBeenPlayedRef = useRef(false);
    const [showMediaPlayer, setShowMediaPlayer] = useState(false);
    const [imageViewerMode, setImageViewerMode] = useState<ImageViewerMode>(mode);
    const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
    const [mainSwiper, setMainSwiper] = useState<Swiper | null>(null);
    const [videoPlaying, setVideoPlaying] = useState(false);

    const [imageViewerOpen, setImageViewerOpen] = useState(false);

    const [imageViewerActiveIndex, setImageViewerActiveIndex] = useState(activeIndex);

    const { isBelowTabletLarge } = useMediaQuery();

    const combinedImages: VehicleService.MarketingImage[] = useMemo(
        () => [...(media?.MarketingImages || []), ...(media?.OperationalImages || [])],
        [media?.MarketingImages, media?.OperationalImages]
    );

    const hasCmsMedia = cmsMedia !== undefined;

    const publish = usePub();
    const handleClose = () => {
        setImageViewerOpen(false);
        publish(EventNames.ImageViewerOpenCompletionEvent, { isOpen: false });
    };

    //Consume set image viewer open request
    const setImageViewerOpenWithCompletionEvent = useSub(
        EventNames.SetImageViewerOpenWithCompletionEvent,
        (data) => {
            const { isOpen } = data;
            setImageViewerOpen(isOpen);
            //publish to alert event complete and feedback current state of image viewer open
            publish(EventNames.ImageViewerOpenCompletionEvent, { isOpen: isOpen });
            return;
        }
    );

    const setImageViewerOpenVoid = useSub(EventNames.SetImageViewerOpen, (data) => {
        const { isOpen } = data;
        setImageViewerOpen(isOpen);
        return;
    });

    const updateImageViewerMode = useSub(EventNames.UpdateImageViewerMode, (data) => {
        const { modeValue } = data;
        setImageViewerMode(modeValue);
        return;
    });

    const updateImageViewerActiveIndex = useSub(EventNames.UpdateImageViewerActiveIndex, (data) => {
        const { index } = data;
        setImageViewerActiveIndex(index);
        return;
    });

    const handleOnSwiperThumbs = (swiper: Swiper) => {
        setThumbsSwiper(swiper);
    };

    useEffect(() => {
        clearTimeout(timer);
        if (
            (videoPlaying && !imageViewerOpen) ||
            (videoHasBeenPlayedRef.current && !imageViewerOpen)
        ) {
            setShowMediaPlayer(false);
            timer = setTimeout(() => {
                setShowMediaPlayer(true);
            }, 100);
        }

        if (videoPlaying && !videoHasBeenPlayedRef.current) {
            videoHasBeenPlayedRef.current = true;
        }

        return () => {
            clearTimeout(timer);
        };
    }, [videoPlaying, imageViewerOpen]);

    useEffect(() => {
        if (imageViewerMode === "360") setShowMediaPlayer(true);
    }, [imageViewerMode]);

    useEffect(() => {
        mainSwiper?.slideTo(imageViewerActiveIndex, 0);
    }, [mainSwiper, imageViewerActiveIndex]);

    useEffect(() => {
        let timeout: NodeJS.Timeout | number | null;

        if (imageViewerOpen && thumbsSwiper && isBelowTabletLarge) {
            timeout = window.setTimeout(() => {
                thumbsSwiper?.slideTo(imageViewerActiveIndex, 100);
            }, 0);
        }

        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [isBelowTabletLarge, imageViewerOpen, thumbsSwiper, imageViewerActiveIndex]);

    useEffect(() => {
        if (!cmsMedia?.length && !combinedImages?.length) return;
        document.documentElement.style.overflow = imageViewerOpen ? "hidden" : "";

        return () => {
            document.documentElement.style.overflow = "";
        };
    }, [cmsMedia?.length, combinedImages?.length, imageViewerOpen]);

    const images = useMemo(() => {
        return hasCmsMedia
            ? cmsMedia.map((cmsImage) => (
                  <SwiperSlide key={cmsImage.alt}>
                      <CMSImage
                          src={cmsImage.src}
                          alt={cmsImage.alt}
                          width={1024}
                          height={683}
                          style={{ objectFit: "contain", height: "auto" }}
                      />
                  </SwiperSlide>
              ))
            : combinedImages.map((image) => (
                  <SwiperSlide key={image.Angle}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                          src={image.Uri}
                          alt={image.Description}
                          width={1024}
                          height={683}
                          style={{ objectFit: "cover", height: "auto" }}
                          loading="lazy"
                      />
                  </SwiperSlide>
              ));
    }, [hasCmsMedia, combinedImages, cmsMedia]);

    return combinedImages?.length || cmsMedia?.length ? (
        <div className={cx("imageViewer", { imageViewerOpen: imageViewerOpen })}>
            <div className={cx("exitButtonContainer")}>
                <button
                    onClick={handleClose}
                    className={cx("exitButton", { is360: imageViewerMode === "360" })}
                    type="button"
                    aria-label={`Exit ${imageViewerMode}`}
                >
                    <UIIcon className={cx("exitButtonIcon")} icon="Close" />
                    <span>Exit {imageViewerMode}</span>
                </button>
            </div>

            <div className={cx("gallery", { galleryActive: imageViewerMode === "Gallery" })}>
                <div className={cx("galleryMain")}>
                    <nav className={cx("galleryMainNavigation")}>
                        <NextPrevButton
                            id="galleryMainPrevButton"
                            direction="Left"
                            ariaLabelText="Previous image"
                        />
                        <NextPrevButton
                            id="galleryMainNextButton"
                            direction="Right"
                            ariaLabelText="Next image"
                        />
                    </nav>
                    <CarouselSwiper
                        navigation={{
                            nextEl: "#galleryMainNextButton",
                            prevEl: "#galleryMainPrevButton",
                        }}
                        onSwiper={setMainSwiper}
                        initialSlide={imageViewerActiveIndex}
                        keyboard={true}
                        loop
                        slidesPerView={1}
                        modules={[FreeMode, Navigation, Thumbs, Keyboard]}
                        thumbs={{
                            swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
                        }}
                    >
                        {images}
                    </CarouselSwiper>
                </div>

                <div className={cx("galleryThumbnails")}>
                    <nav className={cx("galleryThumbnailsNavigationTop")}>
                        <NextPrevButton
                            id="galleryThumbnailPrevButton"
                            direction="Left"
                            ariaLabelText="Previous image"
                        />
                    </nav>
                    <nav className={cx("galleryThumbnailsNavigationBottom")}>
                        <NextPrevButton
                            id="galleryThumbnailNextButton"
                            direction="Right"
                            ariaLabelText="Next image"
                        />
                    </nav>
                    <ThumbsSwiper
                        swiperRef={thumbsSwiper}
                        zoomEnabled={false}
                        combinedImages={combinedImages}
                        cmsMedia={cmsMedia}
                        handleOnSwiperThumbs={handleOnSwiperThumbs}
                        slidesPerView={isBelowTabletLarge || !!cmsMedia ? "auto" : 4}
                        className={cx("thumbsSwiper")}
                        initialSlide={imageViewerActiveIndex}
                    />
                    <MobileScrollGallery
                        className={cx("mobileSwiper")}
                        cmsMedia={cmsMedia}
                        images={combinedImages}
                        initialSlide={imageViewerActiveIndex}
                    />
                    {!!media?.MarketingVideos?.[0]?.Uri && (
                        <Button
                            className={cx("mobileThumbnail360Button")}
                            fullWidth
                            isDisabled={imageViewerMode === "360"}
                            onClick={() => {
                                setShowMediaPlayer(true);
                                setImageViewerMode("360");
                            }}
                            type="button"
                            forceActive={imageViewerMode !== "360"}
                            ariaLabel="360&deg;"
                        >
                            360&deg;
                        </Button>
                    )}
                </div>
            </div>

            <div className={cx("video", { videoActive: imageViewerMode === "360" })}>
                {showMediaPlayer && media?.MarketingVideos?.[0]?.Uri && (
                    <MediaPlayer
                        onPlay={() => setVideoPlaying(true)}
                        onPause={() => setVideoPlaying(false)}
                        onEnded={() => setVideoPlaying(false)}
                        url={media.MarketingVideos[0].Uri}
                        controls={false}
                    />
                )}
            </div>

            <div
                className={cx("switcherContainer", {
                    mobileHideSwitcher: !media?.MarketingVideos?.[0]?.Uri,
                })}
            >
                <div className={cx("switcher")}>
                    {!!media?.MarketingVideos?.[0]?.Uri && (
                        <>
                            <Button
                                fullWidth
                                isDisabled={imageViewerMode === "Gallery"}
                                onClick={() => setImageViewerMode("Gallery")}
                                type="button"
                                forceActive={imageViewerMode !== "Gallery"}
                                ariaLabel="View Gallery"
                            >
                                View Gallery
                            </Button>
                            <Button
                                fullWidth
                                isDisabled={imageViewerMode === "360"}
                                onClick={() => {
                                    setShowMediaPlayer(true);
                                    setImageViewerMode("360");
                                }}
                                type="button"
                                forceActive={imageViewerMode !== "360"}
                                ariaLabel="View 360&deg;"
                            >
                                View 360&deg;
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    ) : null;
};

export default ImageViewer;
